class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this._boundOverlayClose = this.close.bind(this);

    if (this.overlay) {
      this.overlay.addEventListener('click', this._boundOverlayClose);
    }

    this.setHeaderCartIconAccessibility();
  }

  closeCompetingHeaderLayers() {
    const headerSearchPanels = document.querySelectorAll('.rh-mobile-search-panel');
    const headerSearchResults = document.querySelectorAll('.rh-search-results');
    const mobileDrawers = document.querySelectorAll('.rh-mobile-drawer');
    const mobileOpenButtons = document.querySelectorAll('[data-rh-mobile-open]');

    headerSearchPanels.forEach((panel) => {
      panel.classList.remove('is-open');
    });

    headerSearchResults.forEach((results) => {
      results.classList.remove('is-open');
    });

    mobileDrawers.forEach((drawer) => {
      drawer.hidden = true;
      drawer.setAttribute('aria-hidden', 'true');
    });

    mobileOpenButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    document.body.classList.remove('rh-mobile-search-open', 'rh-mobile-menu-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    if (cartLink.dataset.cartDrawerBound === 'true') return;
    cartLink.dataset.cartDrawerBound = 'true';

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.closeCompetingHeaderLayers();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.closeCompetingHeaderLayers();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.closeCompetingHeaderLayers();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    document.body.classList.add('overflow-hidden', 'rh-cart-drawer-open');

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'rh-cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) {
      inner.classList.remove('is-empty');
    }

    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    this.overlay = this.querySelector('#CartDrawer-Overlay');

    if (this.overlay) {
      this.overlay.removeEventListener('click', this._boundOverlayClose);
      this.overlay.addEventListener('click', this._boundOverlayClose);
    }

    setTimeout(() => {
      this.closeCompetingHeaderLayers();
      this.open();
      this.highlightLatestItem();
    }, 30);
  }

  highlightLatestItem() {
    if (!this.productId) return;

    const items = this.querySelectorAll('[data-quantity-variant-id]');
    let targetItem = null;

    items.forEach((input) => {
      const variantId = String(input.getAttribute('data-quantity-variant-id'));
      if (!targetItem && variantId === String(this.productId)) {
        targetItem = input.closest('.drawer__item, .cart-item');
      }
    });

    if (!targetItem) return;

    targetItem.classList.add('is-recently-added');

    targetItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });

    window.setTimeout(() => {
      targetItem.classList.remove('is-recently-added');
    }, 1800);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}